import { Alert, Badge, Box, Progress, Text } from "@mantine/core";
import { IMCBIMAPI } from "@mctypes/mcbim.types";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { FailedAlert } from "../../../../../components";
import { formatMillisecondsToString } from "../../../../utils";
import { formatDateTimeCompact } from "../../selectors";
import { useGetSingleMcBimJobQuery } from "../McBimRTK";

const McBimSingleJob: React.FC<{ jobId: string; projectId: string; onComplete?: () => void }> = ({
  jobId,
  projectId,
  onComplete,
}) => {
  const {
    data: files,
    isLoading: filesLoading,
    error: filesError,
  } = useGetSingleMcBimJobQuery(
    { projectId, jobId },
    {
      pollingInterval: 5000,
    },
  );

  const hasLoadedFiles = files != null;
  const isProcessing = files?.some((job) => job.status === "queued" || job.status === "in-progress") === true;
  const completionReportedRef = React.useRef(false);

  React.useEffect(() => {
    completionReportedRef.current = false;
  }, [jobId]);

  React.useEffect(() => {
    if (hasLoadedFiles === true && isProcessing === false && completionReportedRef.current === false && onComplete != null) {
      completionReportedRef.current = true;
      onComplete();
    }
  }, [hasLoadedFiles, isProcessing, onComplete]);

  const jobFiles = (files ?? []) as unknown as IMCBIMAPI.IJobFile[];
  const filesWithZeroInsertedDocuments =
    jobFiles.filter((file) => file.insertedDocuments === 0 && file.status === "completed").length > 0;

  const columnDefs = React.useMemo<ColDef<IMCBIMAPI.IJobFile>[]>(
    () => [
      {
        field: "filePath",
        headerName: "Sti til fil",
        filter: true,
        flex: 1.4,
        minWidth: 220,
        sortable: true,
      },
      {
        field: "fileName",
        headerName: "Filnavn",
        filter: true,
        flex: 1.4,
        minWidth: 220,
        sortable: true,
      },
      {
        field: "createdAt",
        headerName: "Synkronisering startet",
        valueFormatter: ({ value }) => (value != null ? formatDateTimeCompact(value) : ""),
        filter: true,
        width: 190,
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: ({ value }: ICellRendererParams<IMCBIMAPI.IJobFile, IMCBIMAPI.JobStatus>) => (
          <Badge color={getMantineStatusColor(value)} variant="light">
            {value?.toUpperCase() ?? ""}
          </Badge>
        ),
        filter: true,
        width: 145,
        sortable: true,
      },
      {
        field: "duration",
        headerName: "Tid brukt [min:sek:ms]",
        valueFormatter: ({ value }) => formatMillisecondsToString(value),
        filter: "agNumberColumnFilter",
        width: 190,
        sortable: true,
      },
      {
        field: "insertedDocuments",
        headerName: "Installerte objekter",
        filter: "agNumberColumnFilter",
        width: 170,
        sortable: true,
      },
      {
        field: "errorMessage",
        headerName: "Feilmelding",
        cellRenderer: ({ value }: ICellRendererParams<IMCBIMAPI.IJobFile, string | undefined>) => (
          <Text size="sm" c={value ? "red" : "dimmed"}>
            {value ?? ""}
          </Text>
        ),
        filter: true,
        flex: 1.5,
        minWidth: 240,
        sortable: true,
      },
    ],
    [],
  );

  return (
    <Box>
      {filesError != null && <FailedAlert />}
      {filesWithZeroInsertedDocuments === true && (
        <Alert color="blue" mb="sm">
          Kun filer som ikke allerede er i databasen settes inn.
        </Alert>
      )}
      {(isProcessing === true || filesLoading === true) && (
        <Progress animated value={100} mb="sm" size="xl">
          Filer bearbeides...
        </Progress>
      )}
      {files != null && (
        <Box className="ag-theme-alpine" h={Math.max(180, Math.min(420, 58 + jobFiles.length * 42))}>
          <AgGridReact<IMCBIMAPI.IJobFile>
            rowData={jobFiles}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true }}
            getRowId={({ data }) => `${data.fileStorageId}-${data.createdAt}`}
            animateRows
            localeText={AG_GRID_LOCALE_NOR}
          />
        </Box>
      )}
    </Box>
  );
};

const getMantineStatusColor = (status: IMCBIMAPI.JobStatus) => {
  switch (status) {
    case "failed":
      return "red";
    case "in-progress":
      return "blue";
    case "queued":
      return "yellow";
    case "completed":
      return "green";
    default:
      return "gray";
  }
};

export default McBimSingleJob;
